import { useEffect } from 'react'

// Context-aware help popup. Receives a `contextKey` describing where the
// admin currently is (admin home, or a specific tournament tab) and renders
// a compact card with just that one situation's guidance.

const HELP = {
  home: {
    title: 'Getting Started',
    body: 'Click + New Tournament to create your first tournament. Set the name, date, course, format, tournament days, and bracket days. Then come back here and click the tournament to manage it.',
    actionLabel: '+ New Tournament',
  },
  Overview: {
    title: 'Tournament Overview',
    body: 'High-level snapshot of this tournament — name, course, format, flights and teams. Use the tabs above to drill in.',
  },
  Registration: {
    title: 'Register Teams',
    body: 'Add teams by QR code self-signup, spreadsheet upload, paste from email, or manually. Teams sit here until you move them to flights.',
  },
  Qualifying: {
    title: 'Set Up Qualifying',
    body: 'Choose your qualifying method — round play, handicap, blind draw, or committee assigned. Set the qualifying window open and close dates.',
  },
  Players: {
    title: 'Manage Players',
    body: 'Add or edit individual player records. Pair players into teams from the Flights & Teams tab.',
  },
  'Flights & Teams': {
    title: 'Flight Your Teams',
    body: 'Click Auto-Flight by Qualifying Score — set 8 teams per flight — click Confirm & Assign. Then click Auto-Pair All Flights to set up match play pairings. Then click Auto-Assign All Holes for shotgun start assignments.',
  },
  'Tee Times': {
    title: 'Set Tee Times',
    body: 'Pick the day, type your actual start time (any time — rain delays, late starts, whatever you need), set the interval in minutes, then click Generate Tee Times. Edit any flight manually after generating.',
  },
  'Live Scores': {
    title: 'Monitor Live Scores',
    body: 'Watch all teams scoring in real time. Edit any score if needed. The leaderboard updates automatically as teams submit each hole.',
  },
  Bracket: {
    title: 'Manage the Bracket',
    body: 'Select a flight and click Generate Bracket. After each day’s round is complete click WIN next to each match winner, then click Advance Winners to auto-fill the next day’s matchups. Championship and place labels appear automatically. Use Full Screen to display on the clubhouse TV.',
  },
  Contests: {
    title: 'Track Contests',
    body: 'Record closest-to-the-pin, longest drive, and other side-game winners. Results show up on the public results page.',
  },
  'QR Codes': {
    title: 'Print Scorecards',
    body: 'Print the QR code cards and cut along the dashed lines. Hand one card to each team at the tee. Players scan the QR code on the cart to open their scorecard — no app needed.',
  },
  Results: {
    title: 'Close the Tournament',
    body: 'When all rounds are complete click Close Tournament to archive the final results. The tournament moves to History and is preserved permanently.',
  },
}

export default function HelpWizard({ contextKey, onClose, onAction }) {
  const help = HELP[contextKey] || HELP.home

  // Esc closes — small UX nicety since this isn't a heavy modal.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <>
      {/* Transparent backdrop — click anywhere to dismiss. */}
      <div className="fixed inset-0 z-30" onClick={onClose} />
      <div
        className="fixed top-24 right-4 sm:right-6 z-40 w-[min(22rem,calc(100vw-2rem))] rounded-xl shadow-2xl"
        style={{ backgroundColor: 'var(--ge-primary)', border: '2px solid var(--ge-primary)' }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-4 pt-3 pb-2">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: 'var(--ge-primary)' }}>
              Help
            </p>
            <h3 className="text-base font-extrabold text-white leading-tight">{help.title}</h3>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white text-2xl leading-none -mt-1"
            aria-label="Close">×</button>
        </div>
        <p className="text-sm text-white/90 leading-relaxed px-4 pb-4">{help.body}</p>
        {help.actionLabel && onAction ? (
          <div className="px-4 pb-4">
            <button
              className="w-full rounded-lg px-3 py-2 text-sm font-extrabold uppercase tracking-wider transition active:scale-[0.98]"
              style={{ backgroundColor: 'var(--ge-primary)', color: 'var(--ge-primary)' }}
              onClick={() => { onAction(); onClose() }}>
              {help.actionLabel}
            </button>
          </div>
        ) : null}
      </div>
    </>
  )
}
